/**
 * Tiny formatters shared across pages and apps. Lifted from admin-app in Phase 9b-1.
 */

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Format a YYYY-MM (or YYYY-MM-DD) date as "Mon YYYY". Missing/invalid input → null.
 * Used by the candidate work-history / education display.
 */
export function formatMonthYear(iso: string | null | undefined): string | null {
  if (!iso) return null;
  // Accept "YYYY-MM" by appending -01 so Date parses it consistently across browsers.
  const normalized = /^\d{4}-\d{2}$/.test(iso) ? `${iso}-01` : iso;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * Format a (start, end) pair as "Jan 2023 – Present" / "Jan 2023 – Mar 2024" / etc.
 * If both are missing returns "—".
 */
export function formatMonthYearRange(start: string | null | undefined, end: string | null | undefined): string {
  const s = formatMonthYear(start);
  const e = formatMonthYear(end);
  if (!s && !e) return '—';
  return `${s ?? '?'} – ${e ?? 'Present'}`;
}

export function formatSalary(min: number | null, max: number | null, currencySymbol: string | null | undefined, payPeriod: string | null | undefined): string {
  if (min === null && max === null) return '—';
  const sym = currencySymbol ?? '';
  const fmt = (n: number) => n.toLocaleString();
  let body: string;
  if (min !== null && max !== null) body = `${sym}${fmt(min)} – ${sym}${fmt(max)}`;
  else if (min !== null) body = `From ${sym}${fmt(min)}`;
  else body = `Up to ${sym}${fmt(max as number)}`;
  return payPeriod ? `${body} / ${payPeriod}` : body;
}
